import {useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import DataTable from '../../components/common/DataTable';
import { getApiKeys, addApiKey, revokeApiKey } from '../../services/apiKeyService';
import { handleApiError } from '../../utils/handleApiError';
import * as Yup from 'yup';
import DatePicker from '../../components/common/DatePicker';

const schema = Yup.object({
  ip_address: Yup.string()
    .required('IP address is required')
    .matches(
    /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/,
      'Invalid IP address format'
    ),

  callback_url: Yup.string()
    .required('Callback URL is required') // ✅ NOW REQUIRED
    .url('Invalid URL format'),
});


const statusMap = {
  active:   { label: 'Active',   cls: 'bg-[#D1FAE5] text-[#065F46]' },
  approved: { label: 'Approved', cls: 'bg-[#D1FAE5] text-[#065F46]' },
  revoked:  { label: 'Revoked',  cls: 'bg-[#FEE2E2] text-[#991B1B]' },
  rejected: { label: 'Rejected', cls: 'bg-[#FEE2E2] text-[#991B1B]' },
  pending:  { label: 'Pending',  cls: 'bg-[#FEF3C7] text-[#92400E]' },
};

const StatusBadge = ({ status }) => {
  const s = statusMap[status?.toLowerCase()] || { label: status || '—', cls: 'bg-gray-100 text-gray-600' };
  return (<>
    <span className={`text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-full inline-block whitespace-nowrap ${s.cls}`}>
      {s.label}
    </span>
  </>);
};

export default function MyIps() {
  const [data, setData]                  = useState([]);
  const [loading, setLoading]             = useState(true);
  const [revoking, setRevoking]     = useState(null);
  const [search, setSearch]         = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [ipInput, setIpInput]       = useState('');
  const [urlInput, setUrlInput]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors]         = useState({});
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, has_more: false });

  const resetModal = () => {
    setShowModal(false);
    setIpInput('');
    setUrlInput('');
    setErrors({});
  };

  const fetchApiKeys = (pg = page, lmt = limit, srch = search, from = fromDate, to = toDate) => {
    setLoading(true);
    getApiKeys({
      page: pg,
      limit: lmt,
      search: srch || undefined,
      from_date: from || undefined,
      to_date: to || undefined,
    })
      .then((res) => {
        const payload = res.data?.data || res.data || {};
        const rows = Array.isArray(payload?.requests)
          ? payload.requests
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
        const p = payload?.pagination || {};
        const currentPage = Number(p?.current_page) || pg;
        const lastPage = Number(p?.last_page) || 1;
        setData(rows);
        setPagination({
          current_page: currentPage,
          last_page: lastPage,
          total: Number(p?.total) || rows.length,
          has_more: Boolean(p?.has_more ?? currentPage < lastPage),
        });
      })
      .catch(handleApiError)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchApiKeys(page, limit, search, fromDate, toDate);
  }, [page, limit, search, fromDate, toDate]);

const handleAddSubmit = async () => {
  try {
    setSubmitting(true);
    await schema.validate(
      { ip_address: ipInput.trim(), callback_url: urlInput.trim() },
      { abortEarly: false }
    );
    await addApiKey({ ip_address: ipInput.trim(), callback_url: urlInput.trim() });
    fetchApiKeys(page, limit, search, fromDate, toDate);
    toast.success('IP address added to whitelist.');
    resetModal();
  } catch (err) {
    if (err.name === 'ValidationError') {
      const fieldErrors = {};
      err.inner.forEach(e => { fieldErrors[e.path] = e.message; });
      setErrors(fieldErrors);
    } else {
      handleApiError(err);
    }
  } finally {
    setSubmitting(false);
  }
};

  // const [confirmKey, setConfirmKey] = useState(null);

  // const handleRevoke = (key) => {
  //   if (revoking) return;
  //   setConfirmKey(key);
  // };

  // const confirmRevoke = async () => {
  //   const key = confirmKey;
  //   setConfirmKey(null);
  //   setRevoking(key.request_id);
  //   try {
  //     await revokeApiKey(key.request_id);
  //     toast.success('IP address removed from whitelist.');
  //     fetchApiKeys(page, limit, search, fromDate, toDate);
  //   } catch (err) {
  //     handleApiError(err);
  //   } finally {
  //     setRevoking(null);
  //   }
   
  // };

  const columns = [
    {
      accessorKey: 'request_id',
      header: 'Reference ID',
      size: 160,
      Cell: ({ row }) => (
        <div>
          <div style={{ fontSize: '13px', color: '#111827', fontFamily: 'monospace' }}>
            {String(row.original.request_id || '')}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'ip_address',
      header: 'Infrastructure Details',
      size: 240,
      Cell: ({ row }) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
            <span style={{ fontSize: '13px' }}>🖥</span>
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#111827', fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {row.original.ip_address || '—'}
            </span>
          </div>
          {row.original.callback_url && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px' }}>🔗</span>
              <span style={{ fontSize: '11px', color: '#F59E0B' }}>{row.original.callback_url}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 130,
      Cell: ({ cell }) => <StatusBadge status={cell.getValue()} />,
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      size: 140,
      Cell: ({ cell }) => {
        if (!cell.getValue()) return <span className="text-gray-400">—</span>;
        const d = new Date(cell.getValue());
        return (
          <div>
            <div className="text-[13px] text-gray-900">{d.toLocaleDateString()}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">{d.toLocaleTimeString()}</div>
          </div>
        );
      },
    },
    // {
    //   accessorKey: 'actions',
    //   header: 'Actions',
    //   size: 90,
    //   Cell: ({ row }) => {
    //     const isRevoked  = row.original.status?.toLowerCase() === 'revoked';
    //     const isApproved = row.original.status?.toLowerCase() === 'approved';
    //     const isRevoking = revoking === row.original.request_id;
    //     if (!isApproved) return null;
    //     return (
    //       <button
    //         onClick={() => handleRevoke(row.original)}
    //         disabled={isRevoking}
    //         className={`text-[13px] font-semibold px-2.5 py-1.5 rounded-md border-none transition-colors bg-red-100 text-red-600 hover:bg-red-200 cursor-pointer`}
    //       >
    //         {isRevoking ? '...' : '🗑 Delete'}
    //       </button>
    //     );
    //   },
    // },
  ];

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (newLimit) => {
    setLimit(Number(newLimit));
    setPage(1);
  };

  return (
    <>
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">

          {/* Header */}
          <div className="mb-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-[36px] font-bold text-primary mb-1.5">IP &amp; Webhook Requests</h2>
                {/* <p className="text-[18px] text-[#4C444E] max-w-xl">
                  Secure your API endpoints by whitelisting server IP addresses and configuring real-time callbacks.
                </p> */}
              </div>
                 <button
                  onClick={() => setShowModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-[12px] text-white text-[13px] font-semibold border-none cursor-pointer shadow-[0px_8px_20px_-8px_rgba(234,133,52,0.7)] bg-secondary-gradient hover:opacity-95 transition-opacity"
                  >
                    <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                    Add Request
                  </button>
              </div>
          </div>     
   
          {/* Card */}
          <div className="bg-[#FFFFFF] border border-[#CEC3D01A] shadow-[0px_24px_64px_0px_#4B23640F] rounded-[40px] overflow-hidden">

            {/* Card Header */}
            <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100 flex-wrap gap-3">
              <div>
                <p className="text-[24px] font-bold text-primary mb-0.5">Active Whitelist Nodes</p>
                <p className="text-[12px] text-[#4C444E] font-medium">Manage and monitor your infrastructure connectivity status</p>
              </div>
              <div className="flex items-center gap-3">
                {/* <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><path d="M21 21l-4.35-4.35" strokeLinecap="round"></path></svg>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="bg-[#F3F4F5] rounded-[10px] pl-9 pr-4 py-2 text-[13px] text-[#191C1D] border-none outline-none focus:ring-2 focus:ring-[#4B2364]/30 w-48 transition"
                  />
                </div> */}
                <select
                  value={limit}
                  onChange={(e) => handleLimitChange(e.target.value)}
                  className="bg-[#F3F4F5] rounded-[10px] px-3 py-2 text-[13px] text-[#191C1D] border-none outline-none focus:ring-2 focus:ring-[#4B2364]/30 cursor-pointer"
                >
                  <option value="10">10 / page</option>
                  <option value="25">25 / page</option>
                  <option value="50">50 / page</option>
                  <option value="100">100 / page</option>
                </select>
              </div>
           </div>   

            {/* Table */}
            {loading ? (
              <div className="text-center py-10 text-gray-400 text-sm">Loading API keys…</div>
            ) : (
              <>
                <DataTable columns={columns} data={data} />
                {data.length === 0 && (
                  <div className="text-center py-10 text-gray-400 text-sm">No API keys found.</div>
                )}
              </>
            )}

            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <span className="text-xs text-[#4C444E]">
                  {pagination.total > 0 ? `Showing ${(page - 1) * limit + 1} to ${Math.min(page * limit, pagination.total)} of ${pagination.total} records` : "Showing 0 records"}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>
                  {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                    let pageNum = i + 1;
                    if (pagination.last_page > 5) {
                      if (page > 3) pageNum = page - 2 + i;
                    }
                    return pageNum <= pagination.last_page ? (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                          page === pageNum
                            ? 'bg-primary text-white border border-primary'
                            : 'bg-white text-[#4C444E] border border-[#CEC3D0] hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ) : null;
                  })}
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={!pagination.has_more}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

    </div>

    {showModal && (
      <div className="fixed inset-0 z-50 bg-[#2A123A66] backdrop-blur-[2px] flex items-center justify-center p-4">
        <div className="w-full max-w-[520px] rounded-[28px] overflow-hidden bg-white shadow-[0px_32px_64px_rgba(52,9,77,0.25)]">
          <div className="px-6 py-5 bg-primary-gradient flex items-start justify-between">
            <div>
              <h3 className="text-[32px] leading-tight font-bold text-white">New Whitelisting Request</h3>
              <p className="text-[13px] text-white/80 mt-1">Add secure credentials for your enterprise infrastructure.</p>
            </div>
            <button
              onClick={resetModal}
              className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 border-none text-white cursor-pointer flex items-center justify-center text-sm"
            >
              ×
            </button>
          </div>

          <div className="px-6 py-6 space-y-5">
            <div>
              <label className="block text-[11px] font-bold tracking-widest text-[#4C444E] uppercase mb-2">IP Address</label>
              <input
                type="text"
                value={ipInput}
                onChange={(e) => setIpInput(e.target.value)}
                placeholder="103.21.58.200"
                className="w-full bg-[#D4D6D8] rounded-[14px] px-4 py-3 text-[16px] text-[#1F2937] border-none outline-none"
              />
              {errors.ip_address && <p className="text-red-500 text-xs mt-1">{errors.ip_address}</p>}
            </div>

            <div>
              <label className="block text-[11px] font-bold tracking-widest text-[#4C444E] uppercase mb-2">Callback URL</label>
              <input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://yourserver.com/webhook/Krestapay"
                className="w-full bg-[#D4D6D8] rounded-[14px] px-4 py-3 text-[16px] text-[#1F2937] border-none outline-none"
              />
              {errors.callback_url && <p className="text-red-500 text-xs mt-1">{errors.callback_url}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3 pt-1">
              <button
                onClick={resetModal}
                disabled={submitting}
                className="h-12 rounded-[14px] bg-[#E5E7EB] text-[#3F3F46] text-[16px] font-semibold border-none cursor-pointer disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSubmit}
                disabled={submitting}
                className="h-12 rounded-[14px] text-white text-[16px] font-semibold border-none cursor-pointer bg-secondary-gradient disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Confirm Revoke Modal */}
    {/* {confirmKey && (
      <div className="fixed inset-0 z-50 bg-[#2A123A66] backdrop-blur-[2px] flex items-center justify-center p-4">
        <div className="w-full max-w-[420px] rounded-[28px] overflow-hidden bg-white shadow-[0px_32px_64px_rgba(52,9,77,0.25)]">
          <div className="px-6 py-5 bg-primary-gradient">
            <h3 className="text-[22px] font-bold text-white">Confirm Removal</h3>
            <p className="text-[13px] text-white/80 mt-1">This action cannot be undone.</p>
          </div>
          <div className="px-6 py-6">
            <p className="text-[15px] text-[#191C1D] font-medium mb-6">
              Are you sure you want to remove the IP{" "}
              <span className="font-bold text-[#34094D]">{confirmKey.ip_address}</span> from the whitelist?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setConfirmKey(null)}
                className="h-12 rounded-[14px] bg-[#E5E7EB] text-[#3F3F46] text-[15px] font-semibold border-none cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={confirmRevoke}
                className="h-12 rounded-[14px] text-white text-[15px] font-semibold border-none cursor-pointer bg-red-500 hover:bg-red-600 transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    )} */}

    </>
  );
}
